import React, { useState, useEffect } from 'react';
import {
  Gift, Copy, CheckCircle, Users, Tag,
  Share2, RefreshCw, Plus
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getMyReferralCode, generateLoyaltyVoucher } from '../../../redux/api';

const LoyaltyView = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchLoyaltyData();
  }, []);

  const fetchLoyaltyData = async () => {
    setLoading(true);
    try {
      const referralCodeRes = await getMyReferralCode();
      setReferralData(referralCodeRes.data);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
      toast.error('Greška pri učitavanju loyalty podataka');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code, type = 'referral') => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopiedCode(type);
    toast.success('Kod je kopiran!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleShareReferral = () => {
    const referralCode = referralData?.referralCode;
    if (!referralCode) return;
    const shareText = `Dobrodošli u SpinTasker! Pri registraciji unesite promo kod ${referralCode} — vlasnik koda dobija mesec dana gratis.`;
    if (navigator.share) {
      navigator.share({ title: 'SpinTasker promo kod', text: shareText }).catch(() => {
        handleCopyCode(referralCode);
      });
    } else {
      handleCopyCode(referralCode);
    }
  };

  const handleGenerateVoucher = async () => {
    setGenerating(true);
    try {
      const res = await generateLoyaltyVoucher();
      toast.success(`Novi kod: ${res.data?.voucher?.code || 'generisan'}`);
      await fetchLoyaltyData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri generisanju koda');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const vouchers = referralData?.vouchers || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Gift className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Loyalty program
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Promo kodovi u formatu ime firme + vaučer (npr. {referralData?.prefix || 'FIRMA'}0001)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleGenerateVoucher}
            disabled={generating}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            <span>{generating ? 'Generisanje...' : 'Novi vaučer'}</span>
          </button>
          <button
            onClick={fetchLoyaltyData}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Osveži</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">Aktuelni promo kod</p>
          <code className="text-lg font-mono font-bold text-purple-600 dark:text-purple-400 block mt-2">
            {referralData?.referralCode || '-'}
          </code>
          <div className="mt-4 flex space-x-2">
            <button
              onClick={() => handleCopyCode(referralData?.referralCode, 'referral')}
              className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
            >
              {copiedCode === 'referral' ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span>{copiedCode === 'referral' ? 'Kopirano!' : 'Kopiraj'}</span>
            </button>
            <button
              onClick={handleShareReferral}
              className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
            >
              <Share2 className="h-4 w-4" />
              <span>Podeli</span>
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">Registrovani preko koda</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
            {referralData?.activeReferralsCount || 0}
          </p>
          <Users className="h-8 w-8 text-green-500 mt-2" />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">Meseci gratis</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
            {referralData?.freeMonthsEarned || 0}
          </p>
          <Gift className="h-8 w-8 text-yellow-500 mt-2" />
        </div>
      </div>

      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
          Kako funkcioniše?
        </h2>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          Kod se generiše kao ime firme plus redni broj vaučera: 0001, 0002, 0003...
          Ako novo registrovani korisnik unese taj promo kod pri registraciji, vlasnik koda
          dobija mesec dana gratis pretplate.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">1. Generišite kod</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Prvi kod se pravi automatski. Novi vaučer dobija sledeći broj.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">2. Novi korisnik se registruje</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Unosi vaš promo kod u polje na registraciji.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">3. Dobijte popust</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Automatski dobijate jedan besplatan mesec korišćenja
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Tag className="h-6 w-6 text-purple-500" />
            <span>Vaučeri</span>
          </h2>
        </div>
        <div className="p-6">
          {vouchers.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-400">Još nema generisanih kodova.</p>
          ) : (
            <div className="space-y-3">
              {vouchers.map((voucher) => (
                <div
                  key={voucher._id}
                  className={`border rounded-lg p-4 flex items-center justify-between ${
                    voucher.used
                      ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50'
                      : 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                  }`}
                >
                  <div>
                    <code className="text-lg font-mono font-bold text-gray-900 dark:text-white">
                      {voucher.code}
                    </code>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {voucher.used
                        ? `Iskorišćen ${formatDate(voucher.usedAt)}`
                        : 'Slobodan — podelite novom korisniku'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {voucher.used ? (
                      <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded">Iskorišćen</span>
                    ) : (
                      <button
                        onClick={() => handleCopyCode(voucher.code, voucher._id)}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center space-x-2"
                      >
                        {copiedCode === voucher._id ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        <span>{copiedCode === voucher._id ? 'Kopirano!' : 'Kopiraj'}</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoyaltyView;
